import React, { useState, useEffect } from 'react';
import { Code, Plus, Trash2, Edit2, Check, AlertCircle, Copy, Sparkles, X } from 'lucide-react';
import { auth, db } from '../../firebase/config';
import { BACKEND_URL } from '../../firebase/config';  
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';

const WidgetMaker = () => {
  const [widgetName, setWidgetName] = useState('');
  const [widgetCode, setWidgetCode] = useState('');
  const [availableFor, setAvailableFor] = useState(['hod']);
  const [size, setSize] = useState('full');
  const [description, setDescription] = useState('');
  
  const [existingWidgets, setExistingWidgets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [editMode, setEditMode] = useState(false);
  const [editingWidget, setEditingWidget] = useState(null);
  const [showPromptTemplate, setShowPromptTemplate] = useState(false);

  // Load existing custom widgets
  useEffect(() => {
    loadWidgets();
  }, []);

  const loadWidgets = async () => {
    try {
      const snap = await getDocs(collection(db, 'customWidgets'));
      setExistingWidgets(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error('Error loading widgets:', error);
    }
  };

  const validateCode = (code) => {
    const errors = [];
    
    if (!code.includes('import React')) {
      errors.push('❌ Code must import React');
    }
    if (!code.includes('export default')) {
      errors.push('❌ Code must have "export default" statement');
    }
    if (code.includes('eval(') || code.includes('Function(')) {
      errors.push('⚠️ Code contains dangerous patterns (eval/Function)');
    }
    if (!code.includes(`${widgetName}Widget`)) {
      errors.push(`❌ Component name should be ${widgetName}Widget`);
    }
    if (!code.includes('from \'../../firebase/config\'') && !code.includes('from "../../firebase/config"')) {
      errors.push('⚠️ Firebase imports should use \'../../firebase/config\'');
    }
    
    return errors;
  };

  const handleCreate = async () => {
    setError('');
    setSuccess('');

    if (!widgetName || !widgetCode) {
      setError('Please enter widget name and code');
      return;
    }

    // Validate code
    const validationErrors = validateCode(widgetCode);
    if (validationErrors.length > 0) {
      setError('Code validation failed:\n' + validationErrors.join('\n'));
      return;
    }

    setLoading(true);

    try {
      // Call your backend API
      const response = await fetch(`${BACKEND_URL}/api/widget/create`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await auth.currentUser?.getIdToken()}`
        },
        body: JSON.stringify({
          name: widgetName,
          code: widgetCode,
          availableFor,
          size,
          description,
          createdBy: auth.currentUser?.email
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('✅ Widget created successfully! Refreshing dashboard in 2 seconds...');
        setWidgetName('');
        setWidgetCode('');
        setDescription('');
        await loadWidgets();
        
        // Reload page to load new widget
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setError(data.error || 'Failed to create widget');
      }
    } catch (err) {
      setError('Error: ' + err.message + '\n\nMake sure backend server is running on port 3001');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (widget) => {
    setEditMode(true);
    setEditingWidget(widget);
    setWidgetName(widget.name);
    setWidgetCode(widget.code);
    setAvailableFor(widget.availableFor || ['hod']);
    setSize(widget.size || 'full');
    setDescription(widget.description || '');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleUpdate = async () => {
    setError('');
    setSuccess('');

    if (!widgetName || !widgetCode) {
      setError('Please enter widget name and code');
      return;
    }

    const validationErrors = validateCode(widgetCode);
    if (validationErrors.length > 0) {
      setError('Code validation failed:\n' + validationErrors.join('\n'));
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/widget/${editingWidget.id}/update`, {
        method: 'PUT',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${await auth.currentUser?.getIdToken()}`
        },
        body: JSON.stringify({
          name: widgetName,
          code: widgetCode,
          availableFor,
          size,
          description
        })
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('✅ Widget updated successfully! Refreshing dashboard in 2 seconds...');
        setEditMode(false);
        setEditingWidget(null);
        setWidgetName('');
        setWidgetCode('');
        setDescription('');
        await loadWidgets();
        
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setError(data.error || 'Failed to update widget');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (widgetId, widgetName) => {
    if (!window.confirm(`Delete widget "${widgetName}"?\n\nThis will:\n• Delete the widget file\n• Remove from dashboard\n• Cannot be undone\n\nAre you sure?`)) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || 'http://localhost:3001'}/api/widget/${widgetId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${await auth.currentUser?.getIdToken()}`
        }
      });

      const data = await response.json();

      if (data.success) {
        setSuccess('✅ Widget deleted successfully! Refreshing dashboard in 2 seconds...');
        await loadWidgets();
        
        setTimeout(() => {
          window.location.reload();
        }, 2000);
      } else {
        setError(data.error || 'Failed to delete widget');
      }
    } catch (err) {
      setError('Error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const promptTemplate = `You are generating a React widget for a college mentoring system.

**System Details:**
- React with hooks (functional components)
- Tailwind CSS for styling
- Firebase Firestore for data
- lucide-react for icons
- Component naming: PascalCase + "Widget" suffix

**Available Firestore Collections:**
• students: { rollNumber, firstName, surname, fatherName, division, batch, email, mobile, photoUrl, dob, gender, bloodGroup }
• attendance: { rollNumber, studentName, courses[], overallPercentage, theoryPercentage, practicalPercentage }
• iaTest1: { rollNumber, studentName, subjects[], totalMarks, maxMarks, percentage }
• iaTest2: { rollNumber, studentName, subjects[], totalMarks, maxMarks, percentage }
• users: { email, role, name, department, mobile, staffId }

**Required Imports Template:**
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
import { collection, getDocs, query, where, orderBy, limit } from 'firebase/firestore';
import { IconName } from 'lucide-react';

**Widget Requirements:**
1. Widget Name: [ENTER YOUR WIDGET NAME - e.g., LowAttendance]
2. What to Display: [DESCRIBE WHAT YOU WANT TO SEE]

Example Description:
"Show a table of students whose attendance is below 75%. Include roll number, name, division, and attendance percentage. Sort by attendance lowest first. Use red color for percentages below 75%. Add a search filter by division."

**Code Requirements:**
✅ Complete React functional component
✅ Proper useState and useEffect hooks
✅ Loading state with spinner
✅ Error handling with try-catch
✅ Tailwind CSS styling (bg-white, rounded-lg, shadow-md, p-6)
✅ Responsive design
✅ lucide-react icons
✅ Export default at the end
✅ Component name must be: [YourWidgetName]Widget

**Generate the complete, production-ready code now.**`;

  const copyPromptTemplate = () => {
    navigator.clipboard.writeText(promptTemplate);
    setSuccess('📋 Prompt template copied! Now paste it in ChatGPT or Claude.');
    setTimeout(() => setSuccess(''), 3000);
  };

  const cancelEdit = () => {
    setEditMode(false);
    setEditingWidget(null);
    setWidgetName('');
    setWidgetCode('');
    setDescription('');
    setError('');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6 rounded-lg text-white">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center space-x-3 mb-2">
              <Sparkles className="w-8 h-8" />
              <h2 className="text-3xl font-bold">Widget Maker</h2>
            </div>
            <p className="text-blue-100">
              Create custom dashboard widgets by pasting AI-generated code
            </p>
          </div>
          <button
            onClick={() => setShowPromptTemplate(!showPromptTemplate)}
            className="px-6 py-3 bg-white text-purple-600 rounded-lg hover:bg-blue-50 font-semibold flex items-center space-x-2 shadow-lg"
          >
            <Code className="w-5 h-5" />
            <span>Get Prompt Template</span>
          </button>
        </div>
      </div>

      {/* Prompt Template Modal */}
      {showPromptTemplate && (
        <div className="bg-gradient-to-br from-purple-50 to-blue-50 border-2 border-purple-300 rounded-lg p-6 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-2">
              <Sparkles className="w-6 h-6 text-purple-600" />
              <h3 className="text-xl font-bold text-purple-900">AI Prompt Template</h3>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={copyPromptTemplate}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center space-x-2"
              >
                <Copy className="w-4 h-4" />
                <span>Copy Template</span>
              </button>
              <button
                onClick={() => setShowPromptTemplate(false)}
                className="p-2 hover:bg-purple-100 rounded-lg"
              >
                <X className="w-5 h-5 text-purple-600" />
              </button>
            </div>
          </div>
          
          <div className="bg-white rounded-lg p-4 mb-4 border border-purple-200">
            <p className="text-sm font-semibold text-gray-900 mb-3">📋 How to use:</p>
            <ol className="text-sm text-gray-700 space-y-2 list-decimal list-inside">
              <li>Click <strong>"Copy Template"</strong> button above</li>
              <li>Go to <strong>ChatGPT</strong> (chat.openai.com) or <strong>Claude</strong> (claude.ai)</li>
              <li>Paste the template</li>
              <li>Replace <code className="bg-purple-100 px-2 py-0.5 rounded">[ENTER YOUR WIDGET NAME]</code> with your widget name (e.g., LowAttendance)</li>
              <li>Replace <code className="bg-purple-100 px-2 py-0.5 rounded">[DESCRIBE WHAT YOU WANT TO SEE]</code> with your requirements</li>
              <li>Press Enter and wait for AI to generate code</li>
              <li>Copy the generated code</li>
              <li>Paste it in the "Widget Code" field below</li>
              <li>Click "Create Widget" ✨</li>
            </ol>
          </div>
          
          <div className="bg-gray-900 rounded-lg p-4 overflow-x-auto">
            <pre className="text-green-400 text-xs font-mono whitespace-pre-wrap">{promptTemplate}</pre>
          </div>
        </div>
      )}

      {/* Error/Success Messages */}
      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 flex items-start space-x-3">
          <AlertCircle className="w-6 h-6 text-red-600 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-red-900 font-semibold mb-1">Error</p>
            <p className="text-red-800 whitespace-pre-line text-sm">{error}</p>
          </div>
          <button onClick={() => setError('')} className="text-red-400 hover:text-red-600">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border-2 border-green-200 rounded-lg p-4 flex items-start space-x-3">
          <Check className="w-6 h-6 text-green-600 mt-0.5 flex-shrink-0" />
          <p className="text-green-900 font-semibold">{success}</p>
          <button onClick={() => setSuccess('')} className="text-green-400 hover:text-green-600 ml-auto">
            <X className="w-5 h-5" />
          </button>
        </div>
      )}

      {/* Widget Creation/Edit Form */}
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">
            {editMode ? '✏️ Edit Widget' : '➕ Create New Widget'}
          </h3>
          {editMode && (
            <button
              onClick={cancelEdit}
              className="text-gray-500 hover:text-gray-700"
            >
              Cancel Edit
            </button>
          )}
        </div>

        <div className="space-y-5">
          {/* Widget Name */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Widget Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={widgetName}
              onChange={(e) => setWidgetName(e.target.value.replace(/\s/g, ''))}
              placeholder="e.g., LowAttendance, TopPerformers"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">
              • No spaces allowed (use PascalCase)
              <br />• Component will be named: <code className="bg-gray-100 px-2 py-0.5 rounded">{widgetName || 'YourName'}Widget</code>
            </p>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Description
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="e.g., Shows students with attendance below 75%"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              disabled={loading}
            />
          </div>

          {/* Widget Code */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              Widget Code <span className="text-red-500">*</span>
            </label>
            <textarea
              value={widgetCode}
              onChange={(e) => setWidgetCode(e.target.value)}
              rows={20}
              placeholder="Paste your AI-generated React component code here...

Example:
import React, { useState, useEffect } from 'react';
import { db } from '../../firebase/config';
// ... rest of the code"
              className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg font-mono text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
              disabled={loading}
            />
            <p className="text-xs text-gray-500 mt-1">
              Paste the complete React component code generated by ChatGPT or Claude
            </p>
          </div>

          {/* Settings Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Available For <span className="text-red-500">*</span>
              </label>
              <div className="border-2 border-gray-300 rounded-lg p-3 space-y-2 bg-gray-50">
                {['hod', 'mentoringCoordinator', 'classCoordinator', 'mentor', 'student'].map(role => (
                  <label key={role} className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={availableFor.includes(role)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setAvailableFor([...availableFor, role]);
                        } else {
                          setAvailableFor(availableFor.filter(r => r !== role));
                        }
                      }}
                      className="w-4 h-4 text-blue-600"
                      disabled={loading}
                    />
                    <span className="text-sm text-gray-700 capitalize">
                      {role === 'hod' ? 'HOD' : role.replace(/([A-Z])/g, ' $1').trim()}
                    </span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Widget Size
              </label>
              <select
                value={size}
                onChange={(e) => setSize(e.target.value)}
                className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                disabled={loading}
              >
                <option value="full">Full Width (100%)</option>
                <option value="half">Half Width (50%)</option>
                <option value="third">One Third (33%)</option>
              </select>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-3 pt-4 border-t">
            {editMode ? (
              <>
                <button
                  onClick={handleUpdate}
                  disabled={loading || !widgetName || !widgetCode}
                  className="flex-1 px-6 py-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 font-semibold text-lg"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                      <span>Updating...</span>
                    </>
                  ) : (
                    <>
                      <Check className="w-5 h-5" />
                      <span>Update Widget</span>
                    </>
                  )}
                </button>
                <button
                  onClick={cancelEdit}
                  disabled={loading}
                  className="px-6 py-4 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 disabled:opacity-50 font-semibold"
                >
                  Cancel
                </button>
              </>
            ) : (
              <button
                onClick={handleCreate}
                disabled={loading || !widgetName || !widgetCode}
                className="flex-1 px-6 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-lg hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2 font-semibold text-lg shadow-lg"
              >
                {loading ? (
                  <>
                    <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full" />
                    <span>Creating Widget...</span>
                  </>
                ) : (
                  <>
                    <Plus className="w-6 h-6" />
                    <span>Create Widget</span>
                  </>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Existing Widgets List */}
      <div className="bg-white rounded-lg shadow-lg border border-gray-200 p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-4">
          📦 Custom Widgets ({existingWidgets.length})
        </h3>

        {existingWidgets.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-300">
            <Sparkles className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500 text-lg font-medium">No custom widgets yet</p>
            <p className="text-gray-400 text-sm mt-2">Create your first widget using the form above!</p>
          </div>
        ) : (
          <div className="space-y-3">
            {existingWidgets.map(widget => (
              <div
                key={widget.id}
                className="border-2 border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 transition-all"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h4 className="font-bold text-gray-900 text-lg">{widget.name}</h4>
                    <p className="text-sm text-gray-600 mt-1">{widget.description || 'No description'}</p>
                    <div className="flex flex-wrap gap-3 mt-3 text-xs">
                      <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full font-medium">
                        Size: {widget.size}
                      </span>
                      <span className="px-3 py-1 bg-purple-100 text-purple-700 rounded-full font-medium">
                        Roles: {(widget.availableFor || []).join(', ')}
                      </span>
                      {widget.createdAt && (
                        <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full font-medium">
                          Created: {new Date(widget.createdAt.toDate()).toLocaleDateString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="flex space-x-2 ml-4">
                    <button
                      onClick={() => handleEdit(widget)}
                      className="p-3 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors"
                      title="Edit Widget"
                      disabled={loading}
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                    <button
                      onClick={() => handleDelete(widget.id, widget.name)}
                      className="p-3 text-red-600 hover:bg-red-100 rounded-lg transition-colors"
                      title="Delete Widget"
                      disabled={loading}
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className="bg-blue-50 border-2 border-blue-200 rounded-lg p-6">
        <h4 className="font-bold text-blue-900 mb-3">💡 Quick Tips</h4>
        <ul className="space-y-2 text-sm text-blue-800">
          <li>• Click "Get Prompt Template" to copy the AI prompt</li>
          <li>• Use ChatGPT or Claude to generate widget code</li>
          <li>• Widget names should be in PascalCase (e.g., LowAttendance, TopStudents)</li>
          <li>• Make sure generated code uses the exact widget name you specify</li>
          <li>• Test your widget after creation - you can always edit or delete it</li>
          <li>• Changes require a page reload to take effect</li>
        </ul>
      </div>
    </div>
  );
};

export default WidgetMaker;